import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { DateRange as DayPickerDateRange } from 'react-day-picker';

import { cn } from 'src/lib/utils';
import { Button } from 'src/components/ui/button';
import { Calendar } from 'src/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';

export type MuiDateRange = [Date | null, Date | null];

interface BasicDateRangePickerProps {
  onChange: (value: MuiDateRange) => void;
  onAccept?: (value: MuiDateRange) => void;
  small?: boolean;
  value: MuiDateRange;
}

const toDayPickerRange = (value: MuiDateRange): DayPickerDateRange | undefined => {
  const [from, to] = value;
  if (!from && !to) return undefined;
  return {
    from: from ?? undefined,
    to: to ?? undefined,
  };
};

const formatRange = (value: MuiDateRange) => {
  const [from, to] = value;
  if (from && to) return `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
  if (from) return `${format(from, 'MMM d, yyyy')} – …`;
  return 'Pick a date range';
};

const BasicDateRangePicker: React.FC<BasicDateRangePickerProps> = ({
  onChange,
  onAccept,
  value,
  small,
}) => {
  const [open, setOpen] = React.useState(false);
  // Track the pending in-popover selection so onAccept only fires on close.
  const [draft, setDraft] = React.useState<MuiDateRange>(value);

  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleSelect = (range: DayPickerDateRange | undefined) => {
    const next: MuiDateRange = [range?.from ?? null, range?.to ?? null];
    setDraft(next);
    onChange(next);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && onAccept) {
      onAccept(draft);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal border-[rgba(145,158,171,0.32)]',
            small ? 'h-9' : 'h-14',
            !value[0] && 'text-muted-foreground'
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          <span className="flex-1 truncate">{formatRange(value)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          initialFocus
          mode="range"
          defaultMonth={value[0] ?? undefined}
          selected={toDayPickerRange(draft)}
          onSelect={handleSelect}
          numberOfMonths={2}
          toDate={new Date()}
        />
      </PopoverContent>
    </Popover>
  );
};

export default BasicDateRangePicker;
